import {useCallback, useMemo} from 'react';

import {UseTreeData} from 'modules/decisionsNext/hooks/trees/types';
import {DepartmentLevel} from 'modules/decisionsNext/api/types';

import {DepartmentFilterKeys} from 'core/constants/policyFilter';
import {useDepartmentTreeQuery} from 'modules/decisionsNext/api/trees/departmentTree.generated';
import {groupSelectedKeys, hasChildren, iterateTree, ungroupSelectedKeys} from 'utils/treeUtils';

import {SkipQueryParams, StoreAdapterParams, TreeBasicNode} from 'components/Tree/TreeTypes';
import {useOptimizedSelection} from 'components/Tree/hooks/useOptimizedSelection';

export type DepartmentNode = TreeBasicNode & {
  level?: DepartmentLevel | null;
};

export const useDepartmentsTree: UseTreeData<string, DepartmentNode, SkipQueryParams & {roleId?: string}> = ({
  skip,
  roleId,
} = {}) => {
  const {data, loading} = useDepartmentTreeQuery({
    skip,
    variables: {roleId},
    disableSkipSetsLoadingState: true,
  });
  const treeData = useMemo(() => data?.departments.treeRestricted ?? [], [data]);

  const treeMap = useMemo(() => {
    const map = new Map<string, DepartmentNode>();

    iterateTree(
      treeData,
      (node, parent) => {
        map.set(node.key, {
          key: node.key,
          title: node.title,
          level: node.level,
          parentKey: parent?.key,
          childrenCount: hasChildren(node) ? node.children.length : undefined,
        });
      },
      {withParent: true}
    );

    return map;
  }, [treeData]);

  return {
    treeData,
    treeMap,
    loading,
  };
};

export const useDepartmentTreeSelection = ({
  store,
  setStore,
  skip,
}: StoreAdapterParams<DepartmentFilterKeys> & SkipQueryParams) => {
  const {treeData, loading} = useDepartmentsTree({skip});

  const {departmentIds} = store;
  const selected = useMemo(
    () => Array.from(ungroupSelectedKeys(treeData, departmentIds ?? [])),
    [departmentIds, treeData]
  );
  const setSelected = useCallback(
    (newSelected: string[]) => {
      const groupedSelected = [...groupSelectedKeys(treeData, newSelected)];
      setStore({
        departmentIds: groupedSelected.length ? groupedSelected : undefined,
      });
    },
    [setStore, treeData]
  );
  const setSelectedNone = useCallback(() => {
    setStore({
      departmentIds: undefined,
    });
  }, [setStore]);

  return {
    ...useOptimizedSelection({
      selected,
      setSelected,
      setSelectedNone,
      treeData,
      groupSelection: true,
    }),
    selectedNoneData: {
      departmentIds: undefined,
    },
    treeData,
    isLoading: loading,
  };
};
